import React, { useState, useEffect } from "react";
import { Modal, Form, Input, DatePicker, Select, message } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  FileProtectOutlined,
  CalendarOutlined,
  UserSwitchOutlined,
  BankOutlined,
  NumberOutlined,
  FileDoneOutlined,
} from "@ant-design/icons";
import logger from "../../utils/logger";
import axiosInstance from "../../utils/axios.config";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import locale from "antd/es/date-picker/locale/en_US";
import { format } from "date-fns";
import Swal from "sweetalert2";

// تفعيل plugin للتعامل مع التنسيقات المخصصة
dayjs.extend(customParseFormat);

const { Option } = Select;

const EditClientModal = ({ visible, onClose, onSuccess, client }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    power_of_attorney_date: null,
  });

  // تحديث النموذج عند تغيير العميل
  useEffect(() => {
    if (client && visible) {
      setFormData({
        power_of_attorney_date: client.power_of_attorney_date
          ? dayjs(client.power_of_attorney_date)
          : null,
      });

      form.setFieldsValue({
        ...client,
        power_of_attorney_date: client.power_of_attorney_date
          ? dayjs(client.power_of_attorney_date)
          : null,
      });
    }
  }, [client, visible, form]);

  const handleDateChange = (date) => {
    setFormData((prev) => ({
      ...prev,
      power_of_attorney_date: date,
    }));
  };

  const handleSubmit = async (values) => {
    try {
      setLoading(true);

      // Show loading indicator
      Swal.fire({
        title: "جاري الحفظ...",
        text: "يرجى الانتظار",
        allowOutsideClick: false,
        allowEscapeKey: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const formData = {
        name: values.name?.trim(),
        client_type: values.client_type,
        national_id: values.national_id?.trim(),
        phone: values.phone?.trim(),
        email: values.email?.trim() || null,
        address: values.address?.trim() || null,
        power_of_attorney_number:
          values.power_of_attorney_number?.trim() || null,
        power_of_attorney_date: values.power_of_attorney_date
          ? dayjs(values.power_of_attorney_date).format("YYYY-MM-DD")
          : null,
        power_of_attorney_record:
          values.power_of_attorney_record?.trim() || null,
        notary_name: values.notary_name?.trim() || null,
      };

      const response = await axiosInstance.put(
        `/clients/${client.id}`,
        formData
      );

      if (response.data.success) {
        // Show success message with SweetAlert2
        await Swal.fire({
          title: "تم التعديل!",
          text: "تم تعديل بيانات العميل بنجاح",
          icon: "success",
          timer: 1500,
        });

        form.resetFields();
        onSuccess({
          success: true,
          data: {
            id: client.id,
            name: values.name,
            phone: values.phone,
            email: values.email,
            address: values.address,
            national_id: values.national_id,
            client_type: values.client_type,
            power_of_attorney_date: values.power_of_attorney_date,
            power_of_attorney_number: values.power_of_attorney_number,
            power_of_attorney_record: values.power_of_attorney_record,
            notary_name: values.notary_name
          }
        });
        onClose();
      }
    } catch (error) {
      logger.error("Error details:", error.response || error);

      // Show error message with SweetAlert2
      Swal.fire({
        title: "خطأ!",
        text: error.response?.data?.message || "حدث خطأ في تعديل بيانات العميل",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div className="text-xl font-bold text-right text-gray-800 mb-2 md:mb-4">
          <UserOutlined className="ml-2 text-primary" />
          تعديل بيانات العميل
        </div>
      }
      open={visible}
      onCancel={onClose}
      okText="حفظ التغييرات"
      cancelText="إلغاء"
      onOk={() => form.submit()}
      confirmLoading={loading}
      width="95%"
      style={{ maxWidth: "800px" }}
      dir="rtl"
      className="custom-modal responsive-modal"
      styles={{ 
        body: { maxHeight: "80vh", overflow: "auto", padding: "15px" },
        mask: { backgroundColor: "rgba(0, 0, 0, 0.45)" }
      }}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="px-2 md:px-4"
        requiredMark={false}
        dir="rtl"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 md:gap-4">
          {/* 1. اسم العميل */}
          <Form.Item
            name="name"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                اسم العميل
              </span>
            }
            rules={[{ required: true, message: "الرجاء إدخال اسم العميل" }]}
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<UserOutlined className="ml-2 text-primary" />}
              placeholder="أدخل اسم العميل بالكامل"
              className="h-10 md:h-11 text-right"
              size="middle"
            />
          </Form.Item>

          {/* 2. رقم الهوية */}
          <Form.Item
            name="national_id"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                رقم الهوية
              </span>
            }
            rules={[{ required: true, message: "الرجاء إدخال رقم الهوية" }]}
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<NumberOutlined className="ml-2 text-primary" />}
              placeholder="أدخل رقم الهوية"
              className="text-right h-10 md:h-11"
              size="middle"
              maxLength={10}
            />
          </Form.Item>

          {/* 3. رقم الجوال */}
          <Form.Item
            name="phone"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                رقم الجوال
              </span>
            }
            rules={[
              { required: true, message: "الرجاء إدخال رقم الجوال" },
              {
                pattern: /^07\d{9}$/,
                message: "يجب أن يبدأ الرقم بـ 07 ويتكون من 11 رقم",
              },
            ]}
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<PhoneOutlined className="ml-2 text-primary" />}
              placeholder="07xxxxxxxxx"
              className="text-right h-10 md:h-11"
              size="middle"
              maxLength={11}
            />
          </Form.Item>

          {/* 4. البريد الإلكتروني */}
          <Form.Item
            name="email"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                البريد الإلكتروني
              </span>
            }
            rules={[
              { type: "email", message: "صيغة البريد الإلكتروني غير صحيحة" },
            ]}
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<MailOutlined className="ml-2 text-primary" />}
              placeholder="example@domain.com"
              className="text-right h-10 md:h-11"
              size="middle"
              dir="rtl"
            />
          </Form.Item>

          {/* 5. العنوان */}
          <Form.Item
            name="address"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                العنوان
              </span>
            }
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<HomeOutlined className="ml-2 text-primary" />}
              placeholder="أدخل العنوان التفصيلي"
              className="text-right h-10 md:h-11"
              size="middle"
            />
          </Form.Item>

          {/* 6. نوع العميل */}
          <Form.Item
            name="client_type"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                نوع العميل
              </span>
            }
            rules={[{ required: true, message: "الرجاء اختيار نوع العميل" }]}
            className="mb-2 md:mb-3"
          >
            <Select
              placeholder="اختر نوع العميل"
              className="text-right h-10 md:h-11"
              size="middle"
              suffixIcon={<UserSwitchOutlined className="text-primary" />}
            >
              <Option value="مدعي">مدعي</Option>
              <Option value="مدعى عليه">مدعى عليه</Option>
            </Select>
          </Form.Item>

          {/* 7. رقم الوكالة */}
          <Form.Item
            name="power_of_attorney_number"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                رقم الوكالة
              </span>
            }
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<FileDoneOutlined className="ml-2 text-primary" />}
              placeholder="أدخل رقم الوكالة"
              className="text-right h-10 md:h-11"
              size="middle"
            />
          </Form.Item>

          {/* 8. سجل الوكالة */}
          <Form.Item
            name="power_of_attorney_record"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                سجل الوكالة
              </span>
            }
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<FileProtectOutlined className="ml-2 text-primary" />}
              placeholder="أدخل رقم سجل الوكالة"
              className="text-right h-10 md:h-11"
              size="middle"
            />
          </Form.Item>

          {/* 9. تاريخ الوكالة */}
          <Form.Item
            name="power_of_attorney_date"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                تاريخ الوكالة
              </span>
            }
            className="mb-2 md:mb-3"
          >
            <DatePicker
              placeholder="اختر تاريخ الوكالة"
              style={{ width: "100%" }}
              format="YYYY/MM/DD"
              locale={locale}
              className="text-right h-10 md:h-11"
              size="middle"
              value={formData.power_of_attorney_date}
              onChange={(date) => {
                setFormData(prev => ({
                  ...prev,
                  power_of_attorney_date: date
                }));
                form.setFieldsValue({
                  power_of_attorney_date: date
                });
              }}
              suffixIcon={<CalendarOutlined className="text-primary" />}
              styles={{
                popup: {
                  zIndex: 1060
                }
              }}
              getPopupContainer={(trigger) => trigger.parentElement}
            />
          </Form.Item>

          {/* 10. كاتب العدل */}
          <Form.Item
            name="notary_name"
            label={
              <span className="text-gray-800 font-semibold text-sm md:text-base">
                كاتب العدل
              </span>
            }
            className="mb-2 md:mb-3"
          >
            <Input
              prefix={<BankOutlined className="ml-2 text-primary" />}
              placeholder="أدخل اسم كاتب العدل"
              className="text-right h-10 md:h-11"
              size="middle"
            />
          </Form.Item>
        </div>
      </Form>
    </Modal>
  );
};

export default EditClientModal;
